import { useState } from 'react';

import { AsyncBoundary, Drawer, Icon } from '@kollez/design-system';
import { useTpoReports } from '@/lib/queries';
import { formatLPA } from '@kollez/shared';

/**
 * /tpo/reports (F91a) — direct port of kollez/pages/tpo/reports.html.
 *   Header + batch select + Export CTA
 *   4 KPIs (Placed % / Median / Highest / Companies)
 *   By-branch table with rate bars
 *   By-company + Salary-spread twin cards
 *   Export drawer with 4 options (NIRF / NAAC / Principal's summary / HoD branch report)
 */

const EXPORTS = [
  ['NIRF · Graduation outcomes', '3 years · .xlsx'],
  ['NAAC · Criterion 5.2', '.xlsx and .pdf'],
  ["Principal's summary", 'One page · .pdf'],
  ['Branch report for HoDs', 'One page per branch · .pdf'],
];

export function TpoReportsPage() {
  const { data, isLoading, error, refetch } = useTpoReports();
  const [exportOpen, setExportOpen] = useState(false);
  const [exportChoice, setExportChoice] = useState(0);

  const t = data;
  const placedPct = t?.totals && t.totals.students > 0 ? Math.round((t.totals.placed / t.totals.students) * 1000) / 10 : 0;
  const salaryMax = Math.max(1, ...(t?.salarySpread ?? []).map((s) => s.count));

  return (
    <div className="stack">
      {/* Deep-audit fixes (Page 36/N):
          - Removed the Batch 2026/2025/2024 selector. useTpoReports()
            takes no batch param; the dropdown didn't change the
            fetched numbers, only the subhead text. That's a control
            that promises filtering it can't deliver. Dropped both
            the selector and its 'Batch {batch}' subhead reference.
          - Subhead now names the actual scope of the numbers.
      */}
      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">College reports</h1>
          <p className="subhead">Numbers are derived from released offers and accepted status across every drive at your college.</p>
        </div>
        <div className="ph-actions">
          <button className="btn btn-primary" onClick={() => setExportOpen(true)}>
            <Icon name="download" size={14} />Export
          </button>
        </div>
      </div>

      <AsyncBoundary
        kind="kpis"
        loading={isLoading}
        error={error}
        empty={!isLoading && !t}
        emptyTitle="No data yet"
        emptyHint="Reports fill in as drives complete and offers are accepted."
        onRetry={() => void refetch()}
      >
        {t && (
          <>
            <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
              <div className="card kpi">
                <span className="kpi-label">Placed</span>
                <span className="kpi-value">{placedPct}%</span>
                <span className="kpi-meta">{t.totals.placed} of {t.totals.students}</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Median CTC</span>
                <span className="kpi-value">{t.totals.medianCtc ? formatLPA(t.totals.medianCtc) : '—'}</span>
                <span className="kpi-meta">from accepted offers</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Highest</span>
                <span className="kpi-value">{t.totals.highestCtc ? formatLPA(t.totals.highestCtc) : '—'}</span>
                <span className="kpi-meta">this season</span>
              </div>
              <div className="card kpi">
                <span className="kpi-label">Companies visited</span>
                <span className="kpi-value">{t.totals.companiesVisited}</span>
                <span className="kpi-meta">with at least one drive</span>
              </div>
            </div>

            <section className="card">
              <div className="card-head">
                <h2 className="h-section">By branch</h2>
                <span className="helper">Placed ÷ (students − opted out)</span>
              </div>
              <div className="tbl-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>Branch</th>
                      <th className="r">Students</th>
                      <th className="r hide-mobile">Opted out</th>
                      <th className="r">Placed</th>
                      <th>Rate</th>
                      <th className="r hide-mobile">Median</th>
                      <th className="r hide-mobile">Highest</th>
                    </tr>
                  </thead>
                  <tbody>
                    {t.byBranch.map((b) => {
                      const eligible = Math.max(1, b.students - b.optedOut);
                      const rate = Math.round((b.placed / eligible) * 1000) / 10;
                      return (
                        <tr key={b.code}>
                          <td className="cell-title">{b.code}</td>
                          <td className="r">{b.students}</td>
                          <td className="r hide-mobile">{b.optedOut}</td>
                          <td className="r">{b.placed}</td>
                          <td>
                            <div className="flex items-center gap-2" style={{ minWidth: 120 }}>
                              <div className="bar flex-1"><span style={{ width: `${Math.min(100, rate)}%` }} /></div>
                              <span className="text-sm w-12 text-right">{rate}%</span>
                            </div>
                          </td>
                          <td className="r hide-mobile">{b.medianCtc ? formatLPA(b.medianCtc) : '—'}</td>
                          <td className="r hide-mobile">{b.highestCtc ? formatLPA(b.highestCtc) : '—'}</td>
                        </tr>
                      );
                    })}
                    <tr>
                      <td className="cell-title">All</td>
                      <td className="r">{t.totals.students}</td>
                      <td className="r hide-mobile">{t.byBranch.reduce((s, b) => s + b.optedOut, 0)}</td>
                      <td className="r"><b>{t.totals.placed}</b></td>
                      <td><b className="text-sm">{placedPct}%</b></td>
                      <td className="r hide-mobile">{t.totals.medianCtc ? formatLPA(t.totals.medianCtc) : '—'}</td>
                      <td className="r hide-mobile">{t.totals.highestCtc ? formatLPA(t.totals.highestCtc) : '—'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <div className="grid gap-5 lg:grid-cols-2">
              <section className="card">
                <div className="card-head"><h2 className="h-section">By company</h2></div>
                <div className="tbl-wrap">
                  <table className="tbl">
                    <thead>
                      <tr>
                        <th>Company</th>
                        <th className="r">Offers</th>
                        <th className="r">Accepted</th>
                        <th className="r">CTC</th>
                      </tr>
                    </thead>
                    <tbody>
                      {t.byCompany.map((c) => (
                        <tr key={c.name}>
                          <td>{c.name}</td>
                          <td className="r">{c.offersIssued}</td>
                          <td className="r">{c.offersAccepted}</td>
                          <td className="r">{c.medianCtc ? formatLPA(c.medianCtc) : '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>

              <section className="card card-pad grid gap-3 content-start">
                <h2 className="h-section">Salary spread</h2>
                <div className="grid gap-2">
                  {t.salarySpread.map((s) => (
                    <div key={s.band} className="grid gap-2 items-center text-sm" style={{ gridTemplateColumns: '110px 1fr 40px' }}>
                      <span>{s.band}</span>
                      <div className="bar" style={{ height: 10 }}><span style={{ width: `${(s.count / salaryMax) * 100}%` }} /></div>
                      <span className="text-right">{s.count}</span>
                    </div>
                  ))}
                </div>
                {/* Deep-audit fix (Page 36/N): the old helper hardcoded a
                    narrative claim about the distribution ('Bulk of offers
                    sit in the middle band. The high-CTC tail widens as
                    more product companies visit.') regardless of what the
                    actual chart showed. If a tenant's distribution had a
                    fat low-band or a bimodal pattern, the helper directly
                    contradicted the chart. Removed the invented narrative;
                    the bars speak for themselves. */}
              </section>
            </div>
          </>
        )}
      </AsyncBoundary>

      <Drawer open={exportOpen} onClose={() => setExportOpen(false)} title="Export" size="md">
        <div className="grid gap-3 mt-4">
          {EXPORTS.map(([title, sub], i) => (
            <label key={i} className="card card-pad flex gap-3 items-start cursor-pointer">
              <input
                type="radio"
                name="export"
                className="check mt-1"
                checked={exportChoice === i}
                onChange={() => setExportChoice(i)}
              />
              <div>
                <div className="cell-title">{title}</div>
                <div className="cell-sub">{sub}</div>
              </div>
            </label>
          ))}
        </div>
        {/* Deep-audit fix (Page 36/N): the old Export button fired a
            fake toast — '${EXPORTS[choice]!} — export queued.' — with
            no API call. No jobs enqueued, no file appears. Report
            generation ships in the next release; disabled for now
            so nobody trusts the toast. The chosen export choice is
            kept so once the API lands, the wire-up is one commit. */}
        <div className="drawer-foot">
          <button className="btn btn-outline" onClick={() => setExportOpen(false)}>Cancel</button>
          <button
            className="btn btn-primary ml-auto"
            disabled
            title="Report exports (NIRF, NAAC, principal's summary, HoD branch report) ship in the next release."
          >
            <Icon name="download" size={14} />Export · soon
          </button>
        </div>
      </Drawer>
    </div>
  );
}
